import {
  DEFAULT_ONLINE_FIELD,
  fieldSeatsForMode,
  type OnlineFieldId,
  type OnlineModeId,
  type OnlineRoom,
  type RoomMoveTarget,
  type RoomVisibility,
  type MatchDrawRule,
} from "@golazo/shared";

export type { OnlineRoom, SeatOccupant } from "@golazo/shared";

type RoomListener = (rooms: OnlineRoom[]) => void;

export interface RoomStore {
  readonly clientId: string;
  subscribe(fn: RoomListener): () => void;
  list(): OnlineRoom[];
  refresh(): void;
  create(opts: {
    name: string;
    modeId: OnlineModeId;
    nickname: string;
    visibility?: RoomVisibility;
    fieldId?: OnlineFieldId;
    password?: string;
    /** Formato: lo fija la sala, no la preferencia local. */
    minutes?: number;
    periods?: number;
    onDraw?: MatchDrawRule;
  }): OnlineRoom;
  join(roomId: string, nickname: string, password?: string): OnlineRoom | null;
  /** Cambiarse de asiento (elegir bando). Solo con la sala en el lobby. */
  takeSeat(roomId: string, playerId: string): void;
  leave(roomId: string): void;
  markPlaying(roomId: string): OnlineRoom | null;
  get(roomId: string): OnlineRoom | null;
  /**
   * Ordenes del anfitrion (y moverse uno mismo). Opcionales: solo las tiene la
   * sala de verdad, la del servidor; el almacen local de pruebas no las simula.
   */
  move?(roomId: string, clientId: string, to: RoomMoveTarget): void;
  kick?(roomId: string, clientId: string): void;
  stop?(roomId: string): void;
  dispose(): void;
}

const STORAGE_KEY = "golazo.onlineRooms";
const CHANNEL = "golazo-rooms";

function loadRooms(): OnlineRoom[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as OnlineRoom[];
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    return parsed.filter((r) => now - r.createdAt < 2 * 60 * 60 * 1000 && r.status === "lobby");
  } catch {
    return [];
  }
}

function saveRooms(rooms: OnlineRoom[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rooms));
  } catch {
    // ignore
  }
}

function uid(): string {
  return Math.random().toString(36).slice(2, 9);
}

/** Local-only room store for offline testing (localStorage + BroadcastChannel). */
export function createLocalRoomStore(clientId: string = uid()): RoomStore {
  let rooms = loadRooms();
  const listeners = new Set<RoomListener>();
  const channel =
    typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(CHANNEL) : null;

  const emit = (): void => {
    const snapshot = rooms.map((r) => ({ ...r, seats: { ...r.seats } }));
    for (const l of listeners) l(snapshot);
  };

  const persist = (): void => {
    saveRooms(rooms);
    channel?.postMessage({ type: "sync" });
    emit();
  };

  channel?.addEventListener("message", () => {
    rooms = loadRooms();
    emit();
  });

  const refresh = (): void => {
    rooms = loadRooms();
    emit();
  };

  return {
    clientId,

    subscribe(fn: RoomListener): () => void {
      listeners.add(fn);
      fn(rooms.map((r) => ({ ...r, seats: { ...r.seats } })));
      return () => listeners.delete(fn);
    },

    list(): OnlineRoom[] {
      return rooms.map((r) => ({ ...r, seats: { ...r.seats } }));
    },

    refresh,

    create(opts: {
      name: string;
      modeId: OnlineModeId;
      nickname: string;
      visibility?: RoomVisibility;
      fieldId?: OnlineFieldId;
      password?: string;
      minutes?: number;
      periods?: number;
      onDraw?: MatchDrawRule;
    }): OnlineRoom {
      refresh();
      const seatsList = fieldSeatsForMode(opts.modeId);
      const first = seatsList[0];
      if (!first) throw new Error("Modo sin asientos de campo");

      const room: OnlineRoom = {
        id: uid(),
        name: opts.name.trim() || `Sala ${opts.modeId}`,
        modeId: opts.modeId,
        hostId: clientId,
        seats: { [first.playerId]: { clientId, nickname: opts.nickname } },
        status: "lobby",
        createdAt: Date.now(),
        visibility: opts.visibility ?? "public",
        fieldId: opts.fieldId ?? DEFAULT_ONLINE_FIELD,
        minutes: opts.minutes,
        periods: opts.periods,
        onDraw: opts.onDraw,
      };
      rooms = [room, ...rooms.filter((r) => r.hostId !== clientId)];
      persist();
      return { ...room, seats: { ...room.seats } };
    },

    join(roomId: string, nickname: string): OnlineRoom | null {
      refresh();
      const room = rooms.find((r) => r.id === roomId);
      if (!room || room.status !== "lobby") return null;

      for (const [seatId, occ] of Object.entries(room.seats)) {
        if (occ?.clientId === clientId) {
          room.seats[seatId] = { clientId, nickname };
          persist();
          return { ...room, seats: { ...room.seats } };
        }
      }

      const free = fieldSeatsForMode(room.modeId).find((s) => !room.seats[s.playerId]);
      if (!free) return null;

      room.seats[free.playerId] = { clientId, nickname };
      persist();
      return { ...room, seats: { ...room.seats } };
    },

    takeSeat(roomId: string, playerId: string): void {
      const room = rooms.find((r) => r.id === roomId);
      if (!room || room.status !== "lobby") return;
      if (room.seats[playerId]) return;
      if (!fieldSeatsForMode(room.modeId).some((s) => s.playerId === playerId)) return;

      const current = Object.entries(room.seats).find(([, occ]) => occ?.clientId === clientId);
      if (!current) return;
      const [seatId, occ] = current;
      if (seatId === playerId) return;
      delete room.seats[seatId];
      room.seats[playerId] = occ!;
      persist();
      emit();
    },

    leave(roomId: string): void {
      refresh();
      const room = rooms.find((r) => r.id === roomId);
      if (!room) return;

      for (const [seatId, occ] of Object.entries(room.seats)) {
        if (occ?.clientId === clientId) delete room.seats[seatId];
      }

      if (room.hostId === clientId || Object.keys(room.seats).length === 0) {
        rooms = rooms.filter((r) => r.id !== roomId);
      }
      persist();
    },

    markPlaying(roomId: string): OnlineRoom | null {
      refresh();
      const room = rooms.find((r) => r.id === roomId);
      if (!room || room.hostId !== clientId) return null;
      room.status = "playing";
      persist();
      return { ...room, seats: { ...room.seats } };
    },

    get(roomId: string): OnlineRoom | null {
      refresh();
      const room = rooms.find((r) => r.id === roomId);
      return room ? { ...room, seats: { ...room.seats } } : null;
    },

    dispose(): void {
      channel?.close();
      listeners.clear();
    },
  };
}

/** @deprecated Use createLocalRoomStore */
export const createRoomStore = createLocalRoomStore;

export function occupiedCount(room: OnlineRoom): number {
  return Object.values(room.seats).filter(Boolean).length;
}
